import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  DFP_SPLIT_DEFAULT,
  DFP_SPLIT_MAX,
  DFP_SPLIT_MIN,
  DfpModulesState,
} from '@features/dashboard/performance/pages/dfp/services/dfp-modules.state';

/** Passo das setas do teclado, em pontos percentuais. */
const KEYBOARD_STEP = 5;

/**
 * Beirada arrastável entre as duas colunas da grade da DFP (card 3801), no mesmo gesto do redimensionar coluna de
 * tabela: fica sobre a borda direita de cada card da coluna da esquerda (dentro do vão entre as colunas), o cursor vira
 * `col-resize` e uma linha fina acende no hover e no arraste. Arrastar muda a divisão ao vivo; soltar grava a divisão no
 * navegador (`DfpModulesState.persistSplit`). Duplo clique e a tecla Home voltam para 50/50; as setas andam de 5 em 5
 * pontos. A divisão fica entre 25% e 75%.
 */
@Component({
  selector: 'app-statement-resizer',
  standalone: true,
  template: '<span class="statement-resizer__line" aria-hidden="true"></span>',
  styleUrls: ['./statement-resizer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementResizerComponent {
  private readonly modules = inject(DfpModulesState);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  /** Arraste em andamento (acende a linha). */
  protected readonly dragging = signal(false);
  private grid: HTMLElement | null = null;

  @HostBinding('attr.role') readonly role = 'separator';
  @HostBinding('attr.aria-orientation') readonly orientation = 'vertical';
  /** Só a primeira beirada da grade entra no Tab (as demais mexem na mesma divisão). */
  readonly focusable = input(true);

  @HostBinding('attr.tabindex') get tabIndex(): number {
    return this.focusable() ? 0 : -1;
  }
  @HostBinding('attr.aria-valuemin') readonly valueMin = DFP_SPLIT_MIN;
  @HostBinding('attr.aria-valuemax') readonly valueMax = DFP_SPLIT_MAX;
  @HostBinding('attr.aria-label') readonly label =
    'Largura das colunas. Arraste ou use as setas; Home volta para metade e metade.';
  @HostBinding('attr.title') readonly title =
    'Arraste para ajustar a largura das colunas. Duplo clique volta para metade e metade.';

  @HostBinding('attr.aria-valuenow') get valueNow(): number {
    return this.modules.splitPercent();
  }

  @HostBinding('class.statement-resizer--dragging') get isDragging(): boolean {
    return this.dragging();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.grid = this.host.nativeElement.closest('.statement-modules');
    if (!this.grid) return;
    event.preventDefault();
    this.host.nativeElement.setPointerCapture(event.pointerId);
    this.dragging.set(true);
    // Sem isso o arraste seleciona o texto das tabelas.
    document.body.style.userSelect = 'none';
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.dragging() || !this.grid) return;
    const rect = this.grid.getBoundingClientRect();
    if (!rect.width) return;
    // O meio do vão entre as colunas fica exatamente no percentual da divisão (a coluna da esquerda tem
    // `calc(P% - 0.5rem)` e o vão tem 1rem), então a posição do ponteiro na grade é a divisão.
    const percent = ((event.clientX - rect.left) / rect.width) * 100;
    this.modules.setSplit(percent, { persist: false });
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.dragging()) return;
    this.host.nativeElement.releasePointerCapture?.(event.pointerId);
    this.dragging.set(false);
    document.body.style.userSelect = '';
    window.getSelection()?.removeAllRanges();
    this.grid = null;
    this.modules.persistSplit();
  }

  @HostListener('dblclick')
  onDoubleClick(): void {
    this.modules.setSplit(DFP_SPLIT_DEFAULT);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const current = this.modules.splitPercent();
    const next =
      event.key === 'ArrowLeft'
        ? current - KEYBOARD_STEP
        : event.key === 'ArrowRight'
          ? current + KEYBOARD_STEP
          : event.key === 'Home'
            ? DFP_SPLIT_DEFAULT
            : null;
    if (next === null) return;
    event.preventDefault();
    this.modules.setSplit(next);
  }
}
